import CryptoJS from "crypto-js"; // You'll need: npm install crypto-js

/**
 * Generate unique ID with timestamp + random
 */
export function generateId(prefix = "id") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get current ISO timestamp
 */
export function getTimestamp() {
  return new Date().toISOString();
}

/**
 * Hash base64 data for deduplication
 * @param {string} base64Data - Full data:image/png;base64,... string
 * @returns {string} SHA-256 hash
 */
export function hashImage(base64Data) {
  if (!base64Data) return null;
  return CryptoJS.SHA256(base64Data).toString();
}

/**
 * Extract MIME type from base64 data URL
 */
export function getMimeType(base64Data) {
  const match = base64Data.match(/^data:([^;]+);base64,/);
  return match ? match[1] : "image/png";
}

/**
 * Deep clone object
 * Uses native structuredClone if available (handles Date, Map, Set)
 * Falls back to JSON parse/stringify for older browsers
 */
export function deepClone(obj) {
  // Use native structuredClone if available (Chrome 98+, Firefox 94+, Safari 15.4+)
  if (typeof structuredClone !== 'undefined') {
    try {
      return structuredClone(obj);
    } catch (error) {
      // Fallback if structuredClone fails (e.g., functions, symbols)
      console.warn('structuredClone failed, falling back to JSON:', error);
    }
  }
  
  // Fallback for older browsers or when structuredClone fails
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Compare two objects for equality
 */
export function isEqual(obj1, obj2) {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
}

// ══════════════════════════════════════════════════════════════════════════════
// MONEY CONVERSION HELPERS (FLOATING-POINT FIX)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Convert decimal string to integer cents
 * Prevents floating-point precision errors
 * 
 * @param {string|number} value - "10.50" or 10.50
 * @returns {number} Integer cents (1050)
 * 
 * @example
 * toCents("10.50") // 1050
 * toCents(10.50)   // 1050
 * toCents("0.01")  // 1
 */
export function toCents(value) {
  if (value === null || value === undefined || value === "") {
    return 0;
  }
  
  // Convert to string first to avoid float precision issues
  const strValue = typeof value === 'string' ? value : String(value);
  
  // Parse and convert to cents
  const dollars = parseFloat(strValue);
  
  if (isNaN(dollars)) {
    console.warn(`Invalid money value: ${value}, defaulting to 0`);
    return 0;
  }
  
  // Round to avoid any residual floating-point errors
  return Math.round(dollars * 100);
}

/**
 * Convert integer cents to decimal string
 * 
 * @param {number} cents - Integer cents (1050)
 * @returns {string} Decimal string ("10.50")
 * 
 * @example
 * toDollars(1050) // "10.50"
 * toDollars(1)    // "0.01"
 * toDollars(0)    // "0.00"
 */
export function toDollars(cents) {
  if (cents === null || cents === undefined) {
    return "0.00";
  }
  
  const dollars = cents / 100;
  return dollars.toFixed(2);
}

/**
 * Format cents as currency string with symbol
 * 
 * @param {number} cents - Integer cents
 * @param {string} symbol - Currency symbol (default: "$")
 * @returns {string} Formatted currency ("$10.50")
 */
export function formatCurrency(cents, symbol = "$") {
  return `${symbol}${toDollars(cents)}`;
}